/**
 * Toast.tsx - Notification Toast Component
 *
 * This component displays temporary notification messages with smooth animations.
 * Supports success and error variants with auto-dismiss functionality and manual close.
 *
 * Key Features:
 * - Slide-in/slide-out animations with smooth transitions
 * - Auto-dismiss with configurable duration
 * - Manual close button with hover effects
 * - Progress bar showing remaining time
 * - Success and error variants with different styling
 * - Portal rendering for proper z-index layering
 *
 * Props:
 * - visible: boolean - Whether toast is visible
 * - title: string - Toast message text
 * - variant?: 'success' | 'error' - Toast type (default: success)
 * - duration?: number - Auto-dismiss duration in ms (default: 3000)
 * - onClose: () => void - Close callback handler
 *
 * Dependencies: React Portal, styled-components, Lucide icons
 */

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { CheckCircle, X, XCircle } from "lucide-react";

const slideIn = keyframes`
  from {
    transform: translateY(100%) translateX(-50%);
    opacity: 0;
  }
  to {
    transform: translateY(0) translateX(-50%);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateY(0) translateX(-50%);
    opacity: 1;
  }
  to {
    transform: translateY(100%) translateX(-50%);
    opacity: 0;
  }
`;

const ToastContainer = styled.div<{
  isVisible: boolean;
  isExiting: boolean;
  variant: "success" | "error";
}>`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 999999;

  display: inline-flex; // ✅ change from flex
  align-items: center;
  gap: 12px;
  padding: 16px 24px;

  width: fit-content; // ✅ grow based on content
  max-width: 90vw; // ✅ prevent overflowing screen
  white-space: nowrap; // ✅ prevent text wrapping

  background: ${(props) =>
    props.variant === "error"
      ? "rgba(254, 242, 242, 0.95)"
      : "rgba(255, 255, 255, 0.95)"};

  backdrop-filter: blur(20px);
  border: 1px solid
    ${(props) =>
      props.variant === "error"
        ? "rgba(239, 68, 68, 0.3)"
        : "rgba(255, 255, 255, 0.3)"};

  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);

  animation: ${(props) => {
      if (props.isExiting) return slideOut;
      if (props.isVisible) return slideIn;
      return "none";
    }}
    0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;

  pointer-events: auto;
`;

const IconWrapper = styled.div<{ variant: "success" | "error" }>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => (props.variant === "error" ? "#ef4444" : "#10b981")};
  flex-shrink: 0;
`;

const ToastContent = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ToastTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
  white-space: nowrap;
`;

const formatToastTitle = (title: string): string => {
  if (!title) return "";
  return title.charAt(0).toUpperCase() + title.slice(1).toLowerCase();
};

const ToastMessage = styled.div`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
  line-height: 1.3;
`;

const CloseButton = styled.button<{ variant: "success" | "error" }>`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${(props) => (props.variant === "error" ? "#b91c1c" : "#94a3b8")};
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: ${(props) =>
      props.variant === "error"
        ? "rgba(185, 28, 28, 0.1)"
        : "rgba(148, 163, 184, 0.1)"};
    color: ${(props) => (props.variant === "error" ? "#991b1b" : "#64748b")};
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.9);
  }
`;

const ProgressBar = styled.div<{
  duration: number;
  variant: "success" | "error";
}>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: ${(props) =>
    props.variant === "error"
      ? "linear-gradient(90deg, #ef4444, #dc2626)"
      : "linear-gradient(90deg, #10b981, #059669)"};
  border-radius: 0 0 16px 16px;
  animation: progress ${(props) => props.duration}ms linear forwards;

  @keyframes progress {
    from {
      width: 100%;
    }
    to {
      width: 0%;
    }
  }
`;

interface ToastProps {
  title: string;
  variant?: "success" | "error";
  duration?: number;
  onClose: () => void; 
}

export const Toast: React.FC<ToastProps> = ({
  title,
  variant = "success",
  duration = 3000,
  onClose,
}) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      triggerClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  const triggerClose = () => {
    setIsExiting(true);

    setTimeout(() => {
      onClose();
    }, 300); // must match exit animation
  };

  return (
    <ToastContainer
      isVisible={!isExiting}
      isExiting={isExiting}
      variant={variant}
    >
      <IconWrapper variant={variant}>
        {variant === "error" ? (
          <XCircle size={20} />
        ) : (
          <CheckCircle size={20} />
        )}
      </IconWrapper>

      <ToastContent>
        <ToastTitle>{formatToastTitle(title)}</ToastTitle>
      </ToastContent>

      <CloseButton onClick={triggerClose} variant={variant}>
        <X size={16} />
      </CloseButton>

      <ProgressBar duration={duration} variant={variant} />
    </ToastContainer>
  );
};
